import { cn } from '@/lib/utils'

export interface VoteShareBarProps {
  partyName: string
  partyColor: string
  voteShare: number
  voteLow: number
  voteHigh: number
  isPlayerParty?: boolean
  maxShare?: number
  showLabel?: boolean
  compact?: boolean
  className?: string
}

/**
 * Displays a party's vote share as a horizontal bar with error bars showing
 * the confidence interval (margin of error).
 */
export function VoteShareBar({
  partyName,
  partyColor,
  voteShare,
  voteLow,
  voteHigh,
  isPlayerParty = false,
  maxShare = 100,
  showLabel = true,
  compact = false,
  className,
}: VoteShareBarProps) {
  // Calculate percentages for positioning
  const sharePercent = (voteShare / maxShare) * 100
  const lowPercent = (voteLow / maxShare) * 100
  const highPercent = (voteHigh / maxShare) * 100

  return (
    <div className={cn('space-y-1', className)}>
      {showLabel && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div
              className="h-3 w-3 rounded-sm shrink-0"
              style={{ backgroundColor: partyColor }}
            />
            <span
              className={cn(
                'text-sm truncate',
                isPlayerParty && 'font-semibold'
              )}
            >
              {partyName}
              {isPlayerParty && ' ⭐'}
            </span>
          </div>
          <span className={cn('text-sm tabular-nums', isPlayerParty && 'font-semibold')}>
            {voteShare.toFixed(1)}%
          </span>
        </div>
      )}
      <div className={cn('relative', compact ? 'h-4' : 'h-5')}>
        {/* Background track */}
        <div className="absolute inset-0 bg-muted rounded-full overflow-hidden">
          {/* Main vote share bar */}
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${sharePercent}%`,
              backgroundColor: partyColor,
            }}
          />
        </div>

        {/* Error bar (confidence interval) */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-foreground/20 rounded-full"
          style={{
            left: `${lowPercent}%`,
            width: `${highPercent - lowPercent}%`,
          }}
        />

        {/* Error bar end caps */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-0.5 bg-foreground/40 rounded-full"
          style={{
            left: `${lowPercent}%`,
            height: compact ? '10px' : '14px',
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-0.5 bg-foreground/40 rounded-full"
          style={{
            left: `${highPercent}%`,
            height: compact ? '10px' : '14px',
          }}
        />
      </div>
      {!compact && (
        <div className="text-xs text-muted-foreground text-right">
          [{voteLow.toFixed(1)}% - {voteHigh.toFixed(1)}%]
        </div>
      )}
    </div>
  )
}

/**
 * A simplified version showing just the bar without labels
 * for use in summary cards
 */
export function VoteShareBarCompact({
  partyColor,
  voteShare,
  voteLow,
  voteHigh,
  maxShare = 60,
  className,
}: Omit<VoteShareBarProps, 'partyName' | 'showLabel' | 'compact'>) {
  const sharePercent = (voteShare / maxShare) * 100
  const lowPercent = (voteLow / maxShare) * 100
  const highPercent = (voteHigh / maxShare) * 100

  return (
    <div className={cn('relative h-3', className)}>
      {/* Background track */}
      <div className="absolute inset-0 bg-muted rounded-full overflow-hidden">
        {/* Main vote share bar */}
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(sharePercent, 100)}%`,
            backgroundColor: partyColor,
          }}
        />
      </div>

      {/* Error bar (confidence interval) */}
      <div
        className="absolute top-1/2 -translate-y-1/2 h-1 bg-foreground/20 rounded-full"
        style={{
          left: `${Math.min(lowPercent, 100)}%`,
          width: `${Math.min(highPercent - lowPercent, 100 - lowPercent)}%`,
        }}
      />
    </div>
  )
}
